import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import type { LeadStatus } from '@shared/types';
import { Icon, type IconName } from '@renderer/shared/components/icons';
import { getLeadStatusTone } from './leads-selectors';

export const leadStatusOrder: LeadStatus[] = ['new', 'to_call', 'contacted', 'follow_up', 'qualified', 'converted', 'lost'];

const leadStatusIcons = {
  new: 'plus',
  to_call: 'phone',
  contacted: 'check',
  follow_up: 'timer',
  qualified: 'star',
  converted: 'customers',
  lost: 'times'
} as const satisfies Record<LeadStatus, IconName>;

interface LeadStatusControlProps {
  value: LeadStatus;
  disabled?: boolean;
  leadName: string;
  className?: string;
  t: (key: string, values?: Record<string, string | number>) => string;
  onChange: (status: LeadStatus) => void;
}

export function LeadStatusControl({
  value,
  disabled = false,
  leadName,
  className,
  t,
  onChange
}: LeadStatusControlProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const activeLabel = t(`enums.leadStatus.${value}`);
  const activeTone = getLeadStatusTone(value);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handleDocumentMouseDown(event: globalThis.MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node | null)) {
        setOpen(false);
      }
    }

    function handleDocumentKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [open]);

  function toggleMenu(event: ReactMouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    event.stopPropagation();

    if (!disabled) {
      setOpen((current) => !current);
    }
  }

  function handleSelectStatus(event: ReactMouseEvent<HTMLButtonElement>, status: LeadStatus): void {
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);

    if (status !== value) {
      onChange(status);
    }
  }

  return (
    <div
      ref={rootRef}
      aria-label={t('leads.statusButtons.ariaLabel')}
      className={['av-lead-status-control', open ? 'is-open' : '', className ?? ''].filter(Boolean).join(' ')}
    >
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('leads.statusButtons.current', { lead: leadName, status: activeLabel })}
        className={['av-lead-status-control__active', `av-lead-status-control--${activeTone}`].join(' ')}
        disabled={disabled}
        title={activeLabel}
        type="button"
        onClick={toggleMenu}
      >
        <Icon name={leadStatusIcons[value]} />
        <span>{activeLabel}</span>
      </button>
      {open ? (
        <div className="av-lead-status-control__menu" role="menu" aria-label={t('leads.statusButtons.menuLabel')}>
          {leadStatusOrder
            .filter((status) => status !== value)
            .map((status) => {
              const label = t(`enums.leadStatus.${status}`);

              return (
                <button
                  key={status}
                  aria-label={t('leads.statusButtons.set', { lead: leadName, status: label })}
                  className={[
                    'av-lead-status-control__option',
                    `av-lead-status-control--${getLeadStatusTone(status)}`
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  role="menuitem"
                  type="button"
                  onClick={(event) => handleSelectStatus(event, status)}
                >
                  <Icon name={leadStatusIcons[status]} />
                  <span>{label}</span>
                </button>
              );
            })}
        </div>
      ) : null}
    </div>
  );
}
